import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { LuX } from "react-icons/lu";
import { useIsCompactView } from "../core_stores/useIsCompactViewStore";
import { colors, components, effects, typography } from "../design/tokens";

interface FreeTokensModalContextType {
  show: () => void;
}

const FreeTokensModalContext = createContext<
  FreeTokensModalContextType | undefined
>(undefined);

export function useFreeTokensModal() {
  const ctx = useContext(FreeTokensModalContext);
  if (!ctx)
    throw new Error(
      "useFreeTokensModal must be used within a FreeTokensModalProvider"
    );
  return ctx;
}

export function FreeTokensModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const isCompactView = useIsCompactView();

  useEffect(() => {
    // Prevent background scroll when modal is open and compensate for scrollbar
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
    }, 300);
  };

  const show = useCallback(() => {
    setIsClosing(false);
    setIsOpen(true);
  }, []);

  return (
    <FreeTokensModalContext.Provider value={{ show }}>
      {children}
      {isOpen && (
        <div
          className={`${
            isClosing ? "opacity-0" : "opacity-100"
          } ${!isCompactView && "p-4"} fixed inset-0 z-50
          flex items-center justify-center
          transition-opacity duration-300
          ${components.modal.overlay}`}
          onClick={(e) => {
            if (!isCompactView && e.target === e.currentTarget) {
              handleClose();
            }
          }}
        >
          <div
            className={`${
              isCompactView
                ? "w-full h-full"
                : "max-w-3xl max-h-[90vh] rounded-lg"
            } relative
                    w-full
                    flex flex-col
                    overflow-hidden
                    transform transition-all duration-300 ease-in-out`}
            style={{
              backgroundColor: colors.hex.surfaceSecondary,
              boxShadow: effects.shadow.custom.modal,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={`absolute top-4 right-4 z-50
                ${components.modal.closeButton} text-2xl`}
              onClick={handleClose}
              aria-label="Close"
            >
              <LuX size={24} />
            </button>

            <div className="h-full flex flex-col overflow-y-auto">
              <div className={`${isCompactView ? "pt-14 px-6 pb-10" : "p-8 pb-10"}`}>
                <Content />
              </div>
            </div>
          </div>
        </div>
      )}
    </FreeTokensModalContext.Provider>
  );
}

function Content() {
  return (
    <div>
      {/* Hero Section - Porsche-inspired minimal */}
      <div className="mb-8">
        <p className={`${typography.size.xs} ${typography.weight.medium} ${colors.text.secondary} mb-4 uppercase tracking-widest`}>
          Free Tokens
        </p>
        <p
          className={`text-[56px] ${typography.weight.bold} leading-none mb-4`}
          style={{ color: colors.primary.blue }}
        >
          200,000
        </p>
        <p className={`${typography.size.base} ${colors.text.secondary}`}>
          Share Darvin with your audience and get rewarded.
        </p>
      </div>

      <div
        className="p-4 rounded-lg mb-2"
        style={{ backgroundColor: "rgba(59, 130, 246, 0.08)" }}
      >
        <p className={`${typography.size.sm} ${colors.text.primary}`}>
          Post about Darvin and include a link to Darvin.dev. After we verify
          your post, we'll credit your account with 200,000 free tokens.
        </p>
      </div>

      <hr className={`my-8 ${colors.border.primary}`} />

      <div>
        <p className={`${typography.size.lg} ${typography.weight.semibold} ${colors.text.primary} mb-3`}>How it works?</p>
        <ol className={`list-decimal list-inside ${typography.size.sm} ${colors.text.primary} space-y-2`}>
          <li>
            <b>Create a post</b> on a social media platform with followers
            (e.g., Twitter/X, LinkedIn, Instagram, TikTok, Facebook).
          </li>
          <li>
            <b>Include a link</b> to{" "}
            <span className="underline" style={{ color: colors.primary.blueAlt }}>
              https://darvin.dev
            </span>{" "}
            in the post.
          </li>
          <li>
            <b>Email us your post link</b> to{" "}
            <a
              href="mailto:free-tokens-reward@darvin.dev?subject=Free%20Tokens%20%E2%80%93%20Post%20Verification"
              className="underline"
              style={{ color: colors.primary.blueAlt }}
            >
              free-tokens-reward@darvin.dev
            </a>{" "}
            for verification.
            <ul className="list-disc list-inside ml-4">
              <li>
                Subject: <b>“Free Tokens – Post Verification”</b>
              </li>
              <li>Include in the email:</li>
              <ul className="list-disc list-inside ml-4">
                <li>
                  Your <b>Darvin User ID</b> (find it in Settings → Your
                  Profile)
                </li>
                <li>
                  The <b>direct URL to your social post</b>
                </li>
              </ul>
            </ul>
          </li>
          <li>Once verified, we’ll add the tokens to your account.</li>
        </ol>
      </div>

      <hr className={`my-8 ${colors.border.primary}`} />

      <div>
        <p className={`${typography.size.lg} ${typography.weight.semibold} ${colors.text.primary} mb-3`}>Requirements</p>
        <p className={`${typography.size.sm} ${colors.text.primary} mb-2`}>To keep this fair and prevent abuse:</p>
        <ul className={`list-disc ml-4 ${typography.size.sm} ${colors.text.primary} space-y-1`}>
          <li>
            The social media account must be <b>real and active</b> (not newly
            created for this promo).
          </li>
          <li>
            The account must have a <b>visible follower base</b> (not empty or
            zero-follower).
          </li>
          <li>
            <b>One reward per social account</b> (and per Darvin account).
          </li>
          <li>
            We may decline posts that appear spammy, misleading, or off-topic.
          </li>
        </ul>
      </div>

      <hr className={`my-8 ${colors.border.primary}`} />

      <div>
        <p className={`${typography.size.lg} ${typography.weight.semibold} ${colors.text.primary} mb-3`}>Important Note</p>
        <p className={`${typography.size.sm} ${colors.text.primary} mb-2`}>
          These free bonus tokens do not carry over to future months. Once your
          current subscription month ends, they will expire and reset to the
          standard monthly tier token amount.
        </p>
        <p className={`${typography.size.sm} ${colors.text.primary}`}>
          If you receive free tokens and later purchase your first subscription
          without using them, the bonus tokens (just like the free plan tokens)
          will disappear. Your account will simply switch to the token amount
          included in your purchased subscription.
        </p>
      </div>

      <hr className={`my-8 ${colors.border.primary}`} />

      <div>
        <p className={`${typography.size.base} ${typography.weight.semibold} ${colors.text.primary} mb-2`}>Submit your post:</p>
        <p className={typography.size.sm}>
          <a
            href="mailto:free-tokens-reward@darvin.dev?subject=Free%20Tokens%20%E2%80%93%20Post%20Verification"
            className="underline"
            style={{ color: colors.primary.blueAlt }}
          >
            free-tokens-reward@darvin.dev
          </a>
        </p>
      </div>
    </div>
  );
}
